import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { useAuth } from "@/contexts/AuthContext";
import { ApiError } from "@/lib/api/client";
import {
  courseHomeApi,
  type CourseHomePerspective,
  type CourseHomeProjection,
} from "@/lib/api/courseHome";

export const courseHomeQueryKey = (
  tenantId: string | null | undefined,
  userId: string | null | undefined,
  perspective: CourseHomePerspective,
  courseId: string,
) => ["course-home", tenantId ?? "unresolved", userId ?? "unresolved", perspective, courseId] as const;

/** `enabled` false holds the read until a caller needs it (a sheet over Desk). */
export function useCourseHome(courseId: string, perspective: CourseHomePerspective, enabled = true) {
  const { me } = useAuth();
  const queryClient = useQueryClient();
  const tenantId = me?.tenant_id;
  const userId = me?.user_id;
  const queryKey = courseHomeQueryKey(tenantId, userId, perspective, courseId);
  const identity = queryKey.join(":");
  const [refusedIdentity, setRefusedIdentity] = useState<string | null>(null);
  const refused = refusedIdentity === identity;

  useEffect(() => {
    if (refused) queryClient.removeQueries({ queryKey, exact: true });
  }, [identity, queryClient, queryKey, refused]);

  const query = useQuery({
    queryKey,
    enabled: enabled && Boolean(tenantId && userId && courseId) && !refused,
    retry: (failureCount, error) =>
      error instanceof ApiError && error.status >= 400 && error.status < 500
        ? false
        : failureCount < 1,
    queryFn: async (): Promise<CourseHomeProjection> => {
      try {
        const projection = perspective === "teaching"
          ? await courseHomeApi.getTeaching(courseId)
          : await courseHomeApi.getLearning(courseId);
        setRefusedIdentity((previous) => previous === identity ? null : previous);
        return projection;
      } catch (error) {
        // Only the endpoint's non-disclosing 404 settles that this identity has
        // no relationship to the course. A 401 can be a refreshing credential
        // and a 403 an inactive or unresolved membership; both remain ordinary
        // retryable errors and must not discard the last confirmed projection.
        if (error instanceof ApiError && error.status === 404) {
          setRefusedIdentity(identity);
        }
        throw error;
      }
    },
  });
  return {
    ...query,
    data: refused ? undefined : query.data,
    isPending: refused ? false : query.isPending,
    isNoProjection: refused,
  };
}
